/**
 * A chave de idempotência de um evento do gateway.
 *
 * Um webhook chega mais de uma vez por design: o gateway reentrega, o worker
 * reprocessa, e a reconciliação futura (F049) vai varrer o mesmo período mais
 * de uma vez. `dedupeKeyFor` é a função que decide se dois eventos são "o
 * mesmo" antes de qualquer um deles tocar o banco.
 *
 * A preferência é o `wamid` — o identificador da mensagem do WhatsApp, único
 * por natureza e o que a Master Spec do módulo manda deduplicar por
 * (Coexistência: "Dedupe por `wamid` em tudo"). Quando não há `wamid` — um
 * `account_update`, por exemplo, não tem um — a chave cai para
 * `${kind}:${externalId}`, o identificador que o gateway usa para a própria
 * linha.
 *
 * **Correção da F046, sobre um defeito da F045**: uma mensagem e os eventos
 * de STATUS que chegam depois dela (`sent` → `delivered` → `read`, ou
 * `failed`) compartilham o MESMO wamid — é a mesma mensagem. Uma chave que
 * fosse só o wamid faria o primeiro evento de status colidir com
 * `dedupe_key` do evento da própria mensagem (unique em
 * `communication_gateway_event`) e ser descartado como "duplicata" — e o
 * segundo status colidiria com o primeiro, e assim por diante. Nenhum status
 * jamais seria gravado, e é exatamente disso que a F046 precisa (pricing por
 * wamid). Por isso `kind: 'status'` entra na chave junto do `status` em si:
 * `status:{wamid}:{status}`. Um evento de STATUS repetido com o MESMO status
 * ainda converge (é o comportamento certo); um novo status para o mesmo
 * wamid é, corretamente, uma linha nova.
 *
 * `null` quando nenhum identificador existe. A função não inventa uma chave:
 * um evento sem nenhum identificador não é o mesmo problema que um evento
 * duplicado, e resolver isso é decisão de quem chama — ver o comentário em
 * `communication-openbsp.ts` sobre gravar com uma chave derivada de hash do
 * corpo em vez de descartar em silêncio.
 *
 * **A mesma correção se aplica a `template_status`/`template_category`
 * (F050).** Um modelo pode mudar de status mais de uma vez (`pending` →
 * `approved`, ou `approved` → `paused` meses depois) e pode ser
 * recategorizado mais de uma vez — sem `detail` na chave, o SEGUNDO evento
 * para o mesmo `externalId` (o `message_template_id`) colidiria com o
 * primeiro e nunca seria gravado, exatamente o defeito que motivou o mesmo
 * tratamento em `status` acima.
 */
export const dedupeKeyFor = (input) => {
    if (input.wamid !== null && input.wamid !== '') {
        if (input.kind === 'status') {
            return `status:${input.wamid}:${input.status ?? 'unknown'}`;
        }
        return input.wamid;
    }
    if (input.externalId !== null && input.externalId !== '') {
        if (input.kind === 'template_status' ||
            input.kind === 'template_category') {
            return `${input.kind}:${input.externalId}:${input.detail ?? 'unknown'}`;
        }
        return `${input.kind}:${input.externalId}`;
    }
    return null;
};
/**
 * A janela de atendimento (F046).
 *
 * 24 horas exatas a partir da última mensagem RECEBIDA — não da conta, não
 * da última mensagem em qualquer direção. `lastInboundAt` é a única fonte
 * (ver o comentário em `Conversation.lastInboundAt`): a Meta parou de mandar
 * o objeto `conversation` que dizia "esta janela expira às…" nos webhooks da
 * Graph API v24, então o Aeris conta a janela sozinho.
 *
 * O limite é MEIO-ABERTO: `[lastInboundAt, lastInboundAt + 24h)`. No instante
 * exato de 24 horas depois a janela já está fechada — é a mesma convenção que
 * a Meta documenta ("within 24 hours"), e testar o limite exato é o que prova
 * que não sobrou um `<=` por engano.
 */
const SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;
export const serviceWindow = (input) => {
    if (input.lastInboundAt === null)
        return { open: false, expiresAt: null };
    const expiresAt = new Date(input.lastInboundAt.getTime() + SERVICE_WINDOW_MS);
    return { open: input.at.getTime() < expiresAt.getTime(), expiresAt };
};
/**
 * O prefixo com o nome de quem responde, em cima da mensagem.
 *
 * ```
 * **Thiago:**
 * mensagem
 * ```
 *
 * Só o PRIMEIRO nome, porque é o que soa como uma pessoa falando e não uma
 * assinatura de e-mail. `attendantName` vazio ou nulo devolve o corpo sem
 * prefixo — é o caso do `echo` (o dono mandando pelo próprio celular): essa
 * mensagem já saiu sem prefixo nenhum, e aplicar um aqui, no Aeris, mentiria
 * sobre o que o cliente recebeu.
 *
 * Só no número oficial (F209): no aparelho vinculado (WuzAPI) cada instância é
 * o WhatsApp de uma pessoa, e a rota de envio nem chama esta função.
 *
 * ## Por que DOIS asteriscos, e não um
 *
 * Um asterisco de cada lado é a sintaxe do WhatsApp para negrito, e era o que
 * este código usava — mas o Aeris não fala com o WhatsApp, fala com o gateway.
 * O `whatsapp-dispatcher` do OpenBSP passa todo texto por
 * `markdownToWhatsApp` antes de mandar à Meta, e nessa gramática:
 *
 * - `**texto**` vira negrito;
 * - `*texto*` vira **itálico** (`_texto_`).
 *
 * Então `*Thiago:*` chegava em itálico no celular do cliente. Uma camada foi
 * pulada ao escrever isto, e o defeito só aparece no aparelho de quem recebe —
 * nenhum teste, log ou tela do Aeris o mostrava. Ver F080.
 *
 * ## O que é removido do nome, e por quê
 *
 * `*` e `_` DENTRO do nome são removidos, não escapados: o Markdown não tem
 * escape que sobreviva a esta conversão, e um marcador solto dentro do prefixo
 * abriria ou fecharia formatação no lugar errado — quebrando a mensagem
 * inteira, não só o nome.
 */
export const prefixedBody = (input) => {
    const trimmedName = (input.attendantName ?? '').trim();
    if (trimmedName === '')
        return input.body;
    const firstName = (trimmedName.split(/\s+/)[0] ?? trimmedName).replace(/[*_]/g, '');
    if (firstName === '')
        return input.body;
    return `**${firstName}:**\n${input.body}`;
};
/**
 * Se uma mensagem entra na conta de mensagens cobradas.
 *
 * A Meta expõe dois campos que respondem "isto foi de graça?": `billable`
 * (booleano) e `pricing.type`. **Este código usa só o segundo.** `billable`
 * está no caminho de depreciação da Meta — a documentação oficial (consultada
 * em 2026-08-14, ver Master Plan) já avisa para não depender dele — e
 * `pricing.type` é o campo que sobrevive ao rework de cobrança de
 * 01/10/2026.
 *
 * A regra: tudo que não é explicitamente um dos dois tipos gratuitos cobra.
 * Isso é deliberadamente pessimista — um `pricing.type` novo que a Meta
 * inventar no futuro (e que o Aeris ainda não conhece) é tratado como
 * cobrado, nunca como grátis por omissão. Errar para "isto custa dinheiro" é
 * o lado seguro de um cálculo de custo; o oposto esconderia gasto real.
 */
export const isBillable = (pricing) => pricing.type !== 'free_entry_point' &&
    pricing.type !== 'free_customer_service';
/**
 * Se o Aeris pode mandar uma mensagem livre agora — ou se só resta um
 * template aprovado.
 *
 * Fora da janela de 24h a Cloud API recusa qualquer coisa que não seja um
 * template (`sendTemplate`, F050). A recusa acontece no Aeris, antes de
 * gastar uma chamada ao gateway, com uma frase que explica o porquê em vez de
 * um erro do WhatsApp que ninguém na loja entenderia.
 */
export const canSendFreeform = (input) => {
    if (input.connection === 'linked_device')
        return { allowed: true, reason: null };
    const window = serviceWindow(input);
    if (window.open)
        return { allowed: true, reason: null };
    return {
        allowed: false,
        reason: 'Fora da janela de 24 horas desde a última mensagem do cliente: só um modelo aprovado pela Meta pode ser enviado agora.',
    };
};
export const sendingOptionsFor = (input) => {
    if (input.connection === 'linked_device')
        return 'freeform_only';
    return serviceWindow(input).open ? 'freeform_or_template' : 'template_only';
};
/**
 * Se a conexão sabe falar por MODELO aprovado — e, portanto, por campanha e
 * follow-up, que falam fora da janela por definição (F192, ADR-0039).
 *
 * Só a API oficial. Um aparelho vinculado não tem modelo nenhum, e além disso
 * o WhatsApp não deixa aparelho vinculado INICIAR conversa com quem nunca
 * falou com o número desde julho de 2026 (whatsmeow #1197): uma campanha por
 * ele seria recusada, ou pior, entregue a alguns e custaria o número.
 */
export const connectionSupportsTemplates = (connection) => connection === 'cloud_api';
export const decideContactIdentity = (input) => {
    const { incoming, byLid, byPhone } = input;
    if (incoming.lid === null && incoming.phone === null)
        return null;
    if (byLid === null) {
        if (byPhone === null) {
            return {
                kind: 'create',
                lid: incoming.lid,
                waId: incoming.phone,
                releaseWaIdFrom: null,
            };
        }
        // Só o telefone é conhecido.
        if (incoming.lid === null || byPhone.lid === null) {
            return {
                kind: 'use',
                contactId: byPhone.id,
                setLid: incoming.lid,
                setWaId: null,
                releaseWaIdFrom: null,
            };
        }
        // O contato do telefone já tem OUTRO LID: é outra pessoa que usou este
        // número antes. O número muda de dono; a história dela fica com ela.
        return {
            kind: 'create',
            lid: incoming.lid,
            waId: incoming.phone,
            releaseWaIdFrom: byPhone.id,
        };
    }
    // Só o LID é conhecido: é essa pessoa. O telefone que chegou agora é o atual
    // dela — e nenhum outro contato o tem, senão `byPhone` existiria.
    if (byPhone === null) {
        return {
            kind: 'use',
            contactId: byLid.id,
            setLid: null,
            setWaId: incoming.phone !== null && incoming.phone !== byLid.waId
                ? incoming.phone
                : null,
            releaseWaIdFrom: null,
        };
    }
    if (byLid.id === byPhone.id) {
        return {
            kind: 'use',
            contactId: byLid.id,
            setLid: null,
            setWaId: null,
            releaseWaIdFrom: null,
        };
    }
    // O dono do telefone tem outro LID: outra pessoa. O telefone passa para quem
    // o evento diz que o tem agora.
    if (byPhone.lid !== null) {
        return {
            kind: 'use',
            contactId: byLid.id,
            setLid: null,
            setWaId: incoming.phone,
            releaseWaIdFrom: byPhone.id,
        };
    }
    // Um só-telefone e o contato do LID: metades da mesma pessoa. Fica quem
    // nasceu primeiro; no empate de relógio (duas linhas na mesma transação têm
    // o mesmo `now()`), o id decide — uuidv7 é ordenado pelo instante em que foi
    // gerado.
    const lidOwnerIsOlder = byLid.createdAt.getTime() === byPhone.createdAt.getTime()
        ? byLid.id < byPhone.id
        : byLid.createdAt.getTime() < byPhone.createdAt.getTime();
    const survivor = lidOwnerIsOlder ? byLid : byPhone;
    const loser = lidOwnerIsOlder ? byPhone : byLid;
    return {
        kind: 'merge',
        survivorId: survivor.id,
        loserId: loser.id,
        setLid: survivor.lid === incoming.lid ? null : incoming.lid,
        setWaId: survivor.waId === incoming.phone ? null : incoming.phone,
    };
};
/**
 * ============================================================================
 * Cliente ↔ Party — o matching de telefone (F048)
 * ============================================================================
 *
 * O problema não é comparar dois números — é que o MESMO celular pode estar
 * gravado de formas diferentes nos dois lados:
 *
 *   - A Meta entrega o `wa_id` em E.164 COM código do país, sem o `+`:
 *     `5511987654321` (55 = Brasil, 11 = DDD, 987654321 = o número, com o
 *     nono dígito que o celular ganhou em 2012/2016).
 *   - `normalizePhone` (packages/party/src/entities.ts) só remove
 *     separadores — DELIBERADAMENTE não prefixa `55`, porque um fornecedor
 *     estrangeiro cadastrado sem DDI seria corrompido por essa suposição. Ou
 *     seja, o MESMO cliente pode estar gravado como `11987654321` (sem o
 *     país), `1187654321` (número antigo, sem o nono dígito, sem o país),
 *     `987654321` (só o número, sem DDD nem país — casos legados) ou com o
 *     `55` na frente, se alguém digitou o número completo por hábito.
 *
 * `phoneMatchCandidates` produz as formas que valem a pena comparar; a busca
 * no banco (`findPartiesByPhone`, `@aeris/database`) testa TODAS de uma vez.
 * NADA aqui reescreve um telefone já cadastrado — a normalização acontece só
 * na hora de comparar, nunca no armazenamento (é a mesma regra do plano
 * mestre que proíbe inventar `+55`).
 */
const BRAZIL_COUNTRY_CODE = '55';
/**
 * As formas comparáveis de um `wa_id`, para bater contra o que
 * `party_contact.value` guarda.
 *
 * Cada forma existe por um motivo concreto:
 *
 *   1. **O próprio `wa_id`**, sempre — é a forma que alguém pode ter digitado
 *      por inteiro ao cadastrar o cliente.
 *   2. **Sem o código do país** (`55`), quando o número TEM a forma de um
 *      número brasileiro (começa com `55` e tem 12 ou 13 dígitos no total) —
 *      é a forma que `normalizePhone` produz na imensa maioria dos cadastros,
 *      porque ninguém digita o próprio DDI ao preencher um telefone de
 *      balcão.
 *   3. **Com e sem o nono dígito**, só quando a parte local tem cara de
 *      celular brasileiro (9 dígitos começando em `9`, ou 8 dígitos que
 *      podem ser a mesma linha sem o nono) — porque um cadastro antigo (de
 *      antes de 2012 em algumas áreas, 2016 em todo o país) guarda o número
 *      sem ele, e o WhatsApp sempre entrega o número no formato atual, COM o
 *      nono dígito.
 *
 * **Nunca inventa `55`.** Um `wa_id` que não começa com `55`, ou que começa
 * mas não tem o comprimento de um número brasileiro (DDI + DDD + 8/9
 * dígitos), é tratado como estrangeiro: a única forma devolvida é ele mesmo.
 * É a mesma cautela do comentário em `normalizePhone` — presumir o Brasil
 * corromperia a comparação para quem não é.
 *
 * Devolve formas únicas (sem repetição), na ordem em que foram geradas.
 * String vazia (um `wa_id` sem nenhum dígito) devolve nenhuma forma — não há
 * o que comparar.
 */
export const phoneMatchCandidates = (waId) => {
    const digits = waId.replace(/\D/g, '');
    if (digits === '')
        return [];
    const candidates = new Set([digits]);
    const looksBrazilian = digits.startsWith(BRAZIL_COUNTRY_CODE) &&
        (digits.length === 12 || digits.length === 13);
    if (!looksBrazilian)
        return Array.from(candidates);
    const national = digits.slice(BRAZIL_COUNTRY_CODE.length);
    candidates.add(national);
    const areaCode = national.slice(0, 2);
    const local = national.slice(2);
    // Celular novo (9 dígitos, começando em 9): também gera a forma antiga,
    // sem o nono dígito — com e sem o país.
    if (local.length === 9 && local.startsWith('9')) {
        const localWithoutNinth = local.slice(1);
        candidates.add(`${areaCode}${localWithoutNinth}`);
        candidates.add(`${BRAZIL_COUNTRY_CODE}${areaCode}${localWithoutNinth}`);
    }
    // Número antigo (8 dígitos locais): também gera a forma com o nono dígito
    // inserido — com e sem o país. Vale tanto para o celular que perdeu o
    // dígito no cadastro quanto, por construção, para um fixo — a ambiguidade
    // é aceita aqui porque `resolvePhoneMatch` nunca decide sozinho diante de
    // mais de uma pessoa candidata (ver o comentário lá).
    if (local.length === 8) {
        const localWithNinth = `9${local}`;
        candidates.add(`${areaCode}${localWithNinth}`);
        candidates.add(`${BRAZIL_COUNTRY_CODE}${areaCode}${localWithNinth}`);
    }
    return Array.from(candidates);
};
/**
 * Decide o resultado do matching — e a regra inteira é sobre quando NÃO
 * decidir.
 *
 * `phoneMatchCandidates` é deliberadamente generosa: ela gera formas que
 * PODEM colidir (o exemplo do comentário acima — um fixo de 8 dígitos e um
 * celular novo cujo nono dígito removido dá o mesmo número de outra pessoa).
 * Essa generosidade só é segura porque esta função nunca escolhe sozinha
 * diante de ambiguidade — ela conta quantas PESSOAS DISTINTAS (não quantas
 * linhas de contato) apareceram entre os candidatos:
 *
 *   - **exatamente uma** pessoa (mesmo que ela tenha batido por mais de uma
 *     forma, ou mais de um contato) → `auto`, o único caso em que o Aeris
 *     preenche `party_id` sozinho;
 *   - **mais de uma** pessoa distinta → `ambiguous`. Duas pessoas diferentes
 *     que colidiram numa das formas geradas (o falso positivo do 9º dígito)
 *     produzem exatamente este caso, e é por isso que ele existe: o contato
 *     fica `pending`, esperando um humano decidir, nunca um chute automático
 *     que atribuiria uma conversa à pessoa errada;
 *   - **nenhuma** → `none`, mesmo resultado prático de `ambiguous` (fica
 *     `pending`), mas um motivo diferente — vale a pena a tela distinguir
 *     "ninguém bateu" de "bateu em mais de um".
 *
 * `waId` viaja no parâmetro por completude da assinatura (é o que motivou a
 * busca), mas a decisão em si depende só de `candidates` — o dado que já
 * responde "quantas pessoas existem".
 */
export const resolvePhoneMatch = (input) => {
    const distinctPartyIds = [
        ...new Set(input.candidates.map((candidate) => candidate.partyId)),
    ];
    if (distinctPartyIds.length === 0)
        return { status: 'none', partyId: null };
    if (distinctPartyIds.length === 1) {
        return { status: 'auto', partyId: distinctPartyIds[0] ?? null };
    }
    return { status: 'ambiguous', partyId: null };
};
export const canAssignConversation = (input) => {
    const takingFromSomeoneElse = input.currentAssigneeId !== null &&
        input.currentAssigneeId !== input.targetAssigneeId;
    if (!takingFromSomeoneElse) {
        return {
            allowed: true,
            outcome: input.currentAssigneeId === null ? 'assigned' : 'reassigned',
        };
    }
    if (!input.actorHasAssignPermission) {
        return {
            allowed: false,
            outcome: 'taken',
            reason: 'Sem permissão para tomar uma conversa que já está atribuída a outra pessoa.',
        };
    }
    return { allowed: true, outcome: 'taken' };
};
export const autoAssignee = (input) => {
    if (input.currentAssigneeId !== null)
        return null;
    if (input.accountOwnerId !== null) {
        return { assignee: input.accountOwnerId, source: 'account_owner' };
    }
    if (input.customerSalespersonId !== null) {
        return { assignee: input.customerSalespersonId, source: 'salesperson' };
    }
    return null;
};
const TEMPLATE_STATUS_REFUSAL_MESSAGE = {
    pending: 'Este modelo ainda está em análise pela Meta — a aprovação leva alguns dias. Não é possível enviá-lo ainda.',
    rejected: 'A Meta recusou este modelo. Corrija e submeta um novo.',
    paused: 'A Meta pausou este modelo (qualidade baixa) e ele não pode ser enviado agora.',
    disabled: 'A Meta desativou este modelo. Ele não pode mais ser enviado.',
};
/**
 * Só um modelo `approved` pode ser enviado — é a regra inteira, mas ela
 * precisa existir num lugar só porque um modelo `pending` (a aprovação leva
 * dias), `rejected`, `paused` ou `disabled` são quatro histórias diferentes
 * para o mesmo "não", e quem lê a recusa (um atendente, não um
 * desenvolvedor) precisa saber qual delas é a dele.
 */
export const canSendTemplate = (input) => {
    if (input.template.status !== 'approved') {
        const status = input.template.status;
        return {
            allowed: false,
            refusal: {
                code: `template_${status}`,
                message: TEMPLATE_STATUS_REFUSAL_MESSAGE[status],
            },
        };
    }
    // F066 — um modelo APROVADO com cabeçalho de mídia não pode ser enviado
    // por este gateway ainda. A Cloud API exige um parâmetro de header com o
    // ID de mídia ou um link público do arquivo, e o OpenBSP repassa
    // `content.data` para a Meta SEM pré-processar mídia dentro de
    // `components` (ele só sobe arquivo quando `content.type === 'file'`, o
    // caso de uma mensagem comum). Sem esse parâmetro a Meta recusa o envio.
    //
    // Recusar AQUI, com o motivo em português, em vez de deixar a Meta
    // recusar com um código cru: um erro de gateway na cara do atendente é a
    // mesma classe de silêncio que este módulo já pagou caro. Ver o bloqueio
    // arquitetural registrado em `docs/features/F066-template-media.md`.
    if (input.template.structure !== undefined &&
        templateHeaderFormat(input.template.structure) !== 'TEXT') {
        return {
            allowed: false,
            refusal: {
                code: 'template_media_header',
                message: 'Este modelo tem imagem, vídeo ou documento no cabeçalho, e o gateway ainda não sabe subir mídia — o envio seria recusado pela Meta. Use um modelo de texto por enquanto.',
            },
        };
    }
    return { allowed: true };
};
/**
 * O formato do cabeçalho de um modelo — `TEXT` quando não há cabeçalho
 * nenhum, que é o mesmo caminho de código para quem só quer saber "isto tem
 * mídia?" (F066).
 */
export const templateHeaderFormat = (structure) => {
    const header = structure.components.find((component) => component.type === 'HEADER');
    return header === undefined ? 'TEXT' : header.format;
};
/** Uma variável posicional (`{{1}}`, `{{2}}`…) dentro de um texto de componente. */
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;
/** Os índices posicionais (1-based) referenciados no texto de um componente HEADER/BODY. `null` quando o componente não tem texto. */
const variableIndexesIn = (text) => {
    if (text === null)
        return [];
    return Array.from(text.matchAll(VARIABLE_PATTERN)).map((match) => Number(match[1]));
};
/**
 * Os índices `{{n}}` que um template de fato usa, distintos e em ordem
 * crescente — `[1, 3]` para um modelo que tem `{{1}}` e `{{3}}`.
 *
 * Esta é a lista que define quantos valores o template pede E a que posição
 * cada valor corresponde. As duas coisas juntas, de propósito: separá-las foi
 * o defeito corrigido em `fillTemplateVariables` (ver o comentário lá).
 */
export const templateSlots = (structure) => Array.from(new Set(structure.components.flatMap((component) => component.type === 'HEADER' || component.type === 'BODY'
    ? variableIndexesIn(component.text)
    : []))).sort((a, b) => a - b);
/**
 * Produz os componentes de ENVIO de um template — os VALORES, nunca o texto
 * já substituído.
 *
 * **A confusão que esta função existe para acabar (defeito 11, F055b).**
 * `fillTemplateVariables` devolve os componentes da ESTRUTURA com o texto já
 * substituído (`{ type: 'BODY', text: 'Olá João, ...' }`) — é o formato de
 * CRIAÇÃO de um template, maiúsculo, com `text`, e o único uso correto dele é
 * `communication_message.body` (o que o inbox mostra como "o que foi
 * enviado"). O ENVIO de um template aprovado é outra coisa: a Cloud API (e o
 * OpenBSP atrás dela) quer só os VALORES, em minúsculo, em `parameters` —
 * `{ type: 'body', parameters: [{ type: 'text', text: 'João' }] }`. Antes
 * desta função, os três chamadores (`apps/worker/src/campaign.ts`,
 * `apps/worker/src/followup.ts`, `apps/api/src/routes/v1/
 * communication-templates.ts`) gravavam o resultado de `fillTemplateVariables`
 * DIRETO em `template_payload.components` — o formato de criação sendo
 * mandado como se fosse envio.
 *
 * **A mesma convenção posicional de `fillTemplateVariables`.** `values[0]`
 * preenche o primeiro SLOT do template inteiro (`templateSlots(structure)`),
 * não o primeiro `{{n}}` do componente em que ele aparece — um modelo com
 * `{{1}}` no HEADER e `{{2}}` no BODY tem dois slots, na mesma ordem que
 * `fillTemplateVariables` já usa, para que as duas funções nunca divirjam
 * sobre "qual valor vai em qual variável" quando chamadas com o mesmo
 * `(structure, values)`.
 *
 * Só `HEADER`/`BODY` produzem componente de envio — o mesmo escopo de
 * `templateSlots` (`FOOTER`/`BUTTONS` não carregam variável posicional aqui).
 * Um componente `HEADER`/`BODY` sem nenhuma variável (texto fixo) não entra
 * no resultado: a Cloud API só espera um componente por posição que de fato
 * tem parâmetro.
 *
 * Não recusa nada — quem valida contagem de valores é `fillTemplateVariables`,
 * chamada ANTES desta função em todo call site (o texto renderizado e os
 * componentes de envio nascem do mesmo `(structure, values)` já validado).
 * Um valor faltando (não deveria acontecer, dada a validação prévia) vira
 * string vazia em vez de lançar — o mesmo espírito defensivo do resto do
 * módulo.
 */
export const templateSendComponents = (input) => {
    const slots = templateSlots(input.structure);
    const valueForIndex = new Map(slots.map((slot, position) => [slot, input.values[position] ?? '']));
    const components = [];
    for (const component of input.structure.components) {
        if (component.type !== 'HEADER' && component.type !== 'BODY')
            continue;
        if (component.text === null)
            continue;
        const indexes = Array.from(new Set(variableIndexesIn(component.text))).sort((a, b) => a - b);
        if (indexes.length === 0)
            continue;
        components.push({
            type: component.type === 'HEADER' ? 'header' : 'body',
            parameters: indexes.map((index) => ({
                type: 'text',
                text: valueForIndex.get(index) ?? '',
            })),
        });
    }
    return components;
};
/**
 * Substitui as variáveis posicionais de um template pelos valores informados
 * — e RECUSA quando falta ou sobra valor, em vez de mandar um texto quebrado
 * para o cliente ("Olá {{1}}, seu pedido {{2}} chegou" com só um valor vira
 * "Olá João, seu pedido {{2}} chegou" se esta função não recusasse antes).
 *
 * O número de variáveis ESPERADO é o de índices DISTINTOS referenciados em
 * qualquer componente `HEADER`/`BODY` — contar por conjunto evita dobrar a
 * contagem quando a MESMA variável aparece duas vezes no texto.
 *
 * ## `values` é posicional sobre os SLOTS, não sobre o índice cru
 *
 * `values[0]` preenche o PRIMEIRO slot do template, `values[1]` o segundo, e
 * assim por diante — onde os slots são `templateSlots(structure)`. Num
 * template com `{{1}}` e `{{3}}`, `values[1]` preenche `{{3}}`.
 *
 * A primeira versão contava os índices distintos (certo) mas substituía com
 * `values[index - 1]` (errado), e as duas leituras só coincidem quando os
 * índices são sequenciais. Num modelo com `{{1}}` e `{{3}}` ela esperava 2
 * valores, ACEITAVA 2 valores, e ia procurar `values[2]` — que não existe.
 * O `?? match` então preservava o literal e **o cliente recebia a mensagem
 * com `{{3}}` escrito nela**. Nenhuma recusa, nenhum erro: a função dizia ok
 * e mandava texto quebrado, que é exatamente o que ela existe para impedir.
 *
 * A estrutura vem sincronizada da Meta e um template pode perfeitamente ter
 * índices não sequenciais — não é hipótese de laboratório.
 */
export const fillTemplateVariables = (input) => {
    const slots = templateSlots(input.structure);
    const expectedCount = slots.length;
    if (input.values.length < expectedCount) {
        return {
            ok: false,
            refusal: {
                code: 'missing_variables',
                message: `Este modelo espera ${String(expectedCount)} variável(is) e só ${String(input.values.length)} foram informadas.`,
            },
        };
    }
    if (input.values.length > expectedCount) {
        return {
            ok: false,
            refusal: {
                code: 'extra_variables',
                message: `Este modelo espera ${String(expectedCount)} variável(is) e ${String(input.values.length)} foram informadas.`,
            },
        };
    }
    // O valor de cada ÍNDICE, resolvido pela posição do slot — nunca por
    // `index - 1`, que só acerta quando os índices são sequenciais.
    const valueForIndex = new Map(slots.map((slot, position) => [slot, input.values[position]]));
    const components = input.structure.components.map((component) => {
        if ((component.type !== 'HEADER' && component.type !== 'BODY') ||
            component.text === null) {
            return component;
        }
        const filled = component.text.replace(VARIABLE_PATTERN, (match, digits) => valueForIndex.get(Number(digits)) ?? match);
        return { ...component, text: filled };
    });
    return { ok: true, components };
};
/**
 * ============================================================================
 * Chamados — transições de situação e ordem da fila (F051)
 * ============================================================================
 */
/**
 * As transições legais de um chamado, numa tabela — não espalhadas em `if`s
 * pela rota. Ler esta constante é a resposta completa a "o que pode virar o
 * quê".
 *
 * Duas decisões, tomadas aqui e registradas em
 * docs/features/F051-tickets.md:
 *
 *   1. **`open` não vai direto para `closed`.** Um chamado fechado sem
 *      ninguém ter passado por `in_progress` primeiro é um chamado que
 *      ninguém tocou — fechar sem ter mexido não deixa rastro de trabalho
 *      nenhum para o histórico contar depois. `waiting` também exige o
 *      mesmo caminho: só se espera algo de alguém DEPOIS de já ter
 *      começado a atender.
 *   2. **`closed` só sai por `in_progress`.** Não existe `closed → waiting`
 *      nem `closed → open`: reabrir um chamado fechado é retomar o
 *      atendimento, não fingir que ele nunca tinha sido resolvido. A rota
 *      grava esse caminho específico como evento `reopened` (não
 *      `status_changed`) — ver `communication-tickets.ts`.
 *
 * Uma transição para o MESMO status (`open → open`) nunca é legal — não é
 * uma mudança, é um formulário resubmetido, e tratá-la como transição
 * geraria uma linha de histórico vazia de sentido ("mudou de aberto para
 * aberto").
 */
export const TICKET_TRANSITIONS = {
    open: ['in_progress', 'waiting'],
    in_progress: ['waiting', 'closed'],
    waiting: ['in_progress', 'closed'],
    closed: ['in_progress'],
};
const TICKET_STATUS_LABELS = {
    open: 'aberto',
    in_progress: 'em andamento',
    waiting: 'aguardando',
    closed: 'fechado',
};
/**
 * Decide se um chamado pode ir de `from` para `to` — a única fonte de
 * verdade sobre transições legais, consultada pela rota antes de gravar
 * qualquer coisa. Recusa sempre com `{ code, message }` em português, pronto
 * para a tela mostrar.
 */
export const canTransition = (input) => {
    if (input.from === input.to) {
        return {
            allowed: false,
            refusal: {
                code: 'same_status',
                message: `O chamado já está ${TICKET_STATUS_LABELS[input.from]}.`,
            },
        };
    }
    /*
     * `TICKET_TRANSITIONS` cobre TODA situação de `TicketStatus`, e o tipo é
     * quem garante isso: acrescentar uma situação nova sem a linha dela aqui
     * não compila. Por isso não há um ramo para "situação desconhecida" — ele
     * seria inalcançável, e um ramo inalcançável mente sobre o que pode
     * acontecer. Quem valida a string que vem de fora é a rota, antes de
     * chegar aqui, e o banco tem o check constraint.
     */
    const allowedTargets = TICKET_TRANSITIONS[input.from];
    if (!allowedTargets.includes(input.to)) {
        return {
            allowed: false,
            refusal: {
                code: 'transition_not_allowed',
                message: `Um chamado ${TICKET_STATUS_LABELS[input.from]} não pode ir direto para ${TICKET_STATUS_LABELS[input.to]}.`,
            },
        };
    }
    return { allowed: true };
};
/**
 * A ordem da fila de trabalho — mais urgente primeiro.
 *
 * Uma constante, não um `ORDER BY … CASE` reescrito em cada lugar que
 * ordena chamados: `communication-ticket.ts` (o repositório) constrói o
 * `CASE` do SQL A PARTIR desta lista, então a ordem só existe uma vez.
 */
export const TICKET_PRIORITY_ORDER = [
    'urgent',
    'high',
    'normal',
    'low',
];
/** O rank numérico de uma prioridade — menor é mais urgente. Usado por quem ordena em memória (testes, listas pequenas). */
export const ticketPriorityRank = (priority) => TICKET_PRIORITY_ORDER.indexOf(priority);
/**
 * ============================================================================
 * Robô de IA — a decisão de falar (F052)
 * ============================================================================
 *
 * `shouldBotReply` é uma função PURA: nenhum acesso a banco, nenhum
 * `Date.now()` implícito — tudo que ela precisa entra pelo parâmetro,
 * inclusive o instante (`at`). `packages/llm` fica fora do domínio: esta
 * função decide SE o robô fala, nunca COMO uma chamada de LLM é feita.
 */
/** Os índices de dia que `Intl.DateTimeFormat({weekday:'short'})` em `en-US` pode devolver, mapeados para o `day` (0=domingo) de `BotWorkingHoursWindow`. */
const WEEKDAY_INDEX = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
/**
 * Se `at`, no fuso de `workingHours.timezone`, cai dentro de alguma janela
 * em que o HUMANO atende. Pura: usa `Intl.DateTimeFormat` para ler
 * dia-da-semana e hora NO FUSO configurado a partir de `at`, sem nenhuma
 * chamada de sistema além da conversão de calendário — determinística para
 * o mesmo `(workingHours, at)`.
 *
 * Nenhuma janela configurada ⇒ `false` sempre: sem horário humano definido,
 * o robô pode responder a qualquer momento (dentro dos outros limites).
 *
 * Exportada (F058) — `WorkingHoursEditor` (`apps/web`) usa a MESMA função
 * para a linha viva "esta fila está aberta agora", em vez de reimplementar a
 * comparação de fuso/janela na camada de tela.
 */
export const isHumanWorkingHoursNow = (workingHours, at) => {
    if (workingHours.windows.length === 0)
        return false;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: workingHours.timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(at);
    const weekday = parts.find((part) => part.type === 'weekday')?.value ?? 'Sun';
    const hourRaw = parts.find((part) => part.type === 'hour')?.value ?? '00';
    const minuteRaw = parts.find((part) => part.type === 'minute')?.value ?? '00';
    const day = WEEKDAY_INDEX[weekday] ?? 0;
    // `hour12: false` pode devolver "24" para meia-noite em alguns runtimes —
    // `% 24` normaliza sem mudar nenhum outro valor.
    const minutesNow = (Number(hourRaw) % 24) * 60 + Number(minuteRaw);
    return workingHours.windows.some((window) => {
        if (window.day !== day)
            return false;
        const [startHour, startMinute] = window.start.split(':').map(Number);
        const [endHour, endMinute] = window.end.split(':').map(Number);
        const startMinutes = (startHour ?? 0) * 60 + (startMinute ?? 0);
        const endMinutes = (endHour ?? 0) * 60 + (endMinute ?? 0);
        return minutesNow >= startMinutes && minutesNow < endMinutes;
    });
};
/**
 * O horário EFETIVO de uma fila (F058): o dela própria, se tiver um
 * configurado, ou o do robô/tenant senão. `null` e `undefined` em
 * `queue.workingHours` são o MESMO caso — "esta fila não sobrescreve" —
 * por isso `??`, nunca uma checagem de `null` isolada que trataria os dois
 * jeitos de "ausente" de forma diferente.
 *
 * Pura, e é o que fecha a lacuna que a F047 deixou: `communication_queue.
 * working_hours` existia desde a migração 0034, tinha rótulo "Horário
 * (JSON)" na tela e não era lida por ninguém — `shouldBotReply` recebia da
 * fila só `{botFirst}`. Esta função é o que `shouldBotReply` (abaixo) e
 * `apps/worker/src/bot.ts` passam a usar.
 */
export const effectiveWorkingHours = (input) => input.queue?.workingHours ?? input.config.workingHours;
/**
 * Agrupa `windows` em sete linhas (domingo→sábado) — SEMPRE sete, mesmo para
 * um dia sem nenhuma faixa (`ranges: []`), porque o editor precisa de uma
 * linha para cada dia da semana independente do que está gravado.
 *
 * Não tenta remontar uma faixa que atravessa a meia-noite: se `windows`
 * carrega `{day:2, 22:00, 24:00}` e `{day:3, 00:00, 02:00}` (o resultado de
 * `splitOvernightRange`, abaixo), esta função devolve as DUAS faixas
 * separadas, nas linhas de terça e quarta — a verdade do que está gravado,
 * nunca uma reconstrução da intenção original de quem digitou.
 */
export const workingHoursToDayRows = (windows) => Array.from({ length: 7 }, (_, day) => ({
    day,
    ranges: windows
        .filter((window) => window.day === day)
        .map((window) => ({ start: window.start, end: window.end })),
}));
/** O inverso de `workingHoursToDayRows` — achata as linhas de volta num array de janelas, na ordem das linhas e, dentro de cada uma, na ordem das faixas. */
export const dayRowsToWindows = (rows) => rows.flatMap((row) => row.ranges.map((range) => ({
    day: row.day,
    start: range.start,
    end: range.end,
})));
/**
 * Uma faixa `start`–`end` digitada por uma pessoa num dia, convertida nas
 * janelas de fato gravadas — DUAS quando ela atravessa a meia-noite
 * (`end <= start`, textualmente — a mesma comparação que
 * `WorkingHoursWindowSchema`, `@aeris/contracts`, usa para recusar uma
 * janela crua).
 *
 * **Por que duas, nunca uma só com `end < start`.** `isHumanWorkingHoursNow`
 * compara `agora >= start && agora < end`: uma janela `22:00`–`02:00`
 * sozinha NUNCA casa, porque não existe minuto em que `minutesNow >= 22:00`
 * E `minutesNow < 02:00` ao mesmo tempo — a comparação falha SEMPRE, em
 * silêncio, sem erro nenhum avisando. O editor evita isso na origem: divide
 * em `{day, start, "24:00"}` (o resto do dia digitado) e
 * `{(day+1)%7, "00:00", end}` (o começo do dia seguinte) — `% 7` fecha o
 * ciclo do sábado (6) de volta para o domingo (0).
 */
export const splitOvernightRange = (input) => {
    if (input.end > input.start) {
        return [{ day: input.day, start: input.start, end: input.end }];
    }
    return [
        { day: input.day, start: input.start, end: '24:00' },
        { day: (input.day + 1) % 7, start: '00:00', end: input.end },
    ];
};
export const shouldBotReply = (input) => {
    /*
     * O GUARDA ANTI-LOOP (Objetivo 2 da F052): o robô só reage a mensagem
     * INBOUND — nunca a `bot` (ele mesmo), `echo` (o dono no celular),
     * `campaign`, `followup`, `system` ou qualquer outro origin de SAÍDA. Não
     * checar `origin === 'agent'` de propósito: checar `direction === 'in'`
     * cobre TODOS os origins de saída de uma vez, sem depender de uma lista
     * que alguém tem que lembrar de manter completa toda vez que um origin
     * novo nascer. Na prática este ramo é inalcançável — só uma mensagem de
     * ENTRADA enfileira `bot.respond` (o mesmo caminho que já processa a
     * mensagem recebida) — mas a função continua total, e `'error'` é a
     * razão certa para "isto não deveria ter acontecido".
     */
    if (input.message.direction !== 'in') {
        return { reply: false, reason: 'error' };
    }
    // Robô desligado para este tenant/fila: um humano cuida de tudo.
    if (!input.config.enabled) {
        return { reply: false, reason: 'human_took_over' };
    }
    // A conversa já tem um responsável humano: o robô não fala por cima de
    // quem já assumiu.
    if (input.conversation.assigneeEmployeeId !== null) {
        return { reply: false, reason: 'human_took_over' };
    }
    // Um handoff anterior nesta conversa ainda não foi atendido por um humano
    // — ver o comentário no parâmetro `pendingHandoff` acima. Checado ANTES da
    // janela de 24h de propósito: mesmo que a janela reabra com uma mensagem
    // nova do cliente, o pedido de handoff continua de pé até alguém responder.
    if (input.pendingHandoff) {
        return { reply: false, reason: 'handoff_pending' };
    }
    // Fora da janela de 24h: só um modelo aprovado pode ser enviado (F050), e
    // o robô não manda template — ele espera o handoff.
    if (!serviceWindow({
        lastInboundAt: input.conversation.lastInboundAt,
        at: input.at,
    }).open) {
        return { reply: false, reason: 'window_closed' };
    }
    // O horário EFETIVO: o da fila, se ela sobrescrever; senão o do
    // robô/tenant (F058, `effectiveWorkingHours`). É horário de atendimento
    // humano agora — mas uma fila com `botFirst` deixa o robô responder mesmo
    // assim (a decisão do dono, ver o comentário no parâmetro `queue` acima).
    // Sem fila, ou fila com `botFirst: false`, o horário humano cala o robô.
    const workingHours = effectiveWorkingHours({
        queue: input.queue,
        config: input.config,
    });
    if (isHumanWorkingHoursNow(workingHours, input.at) &&
        !input.queue?.botFirst) {
        return { reply: false, reason: 'human_took_over' };
    }
    // O cliente pediu gente — a checagem é por substring, sem diferenciar
    // maiúsculas, porque é assim que alguém escreve no WhatsApp.
    const bodyLower = (input.messageBody ?? '').toLowerCase();
    const askedForHuman = input.config.handoffKeywords.some((keyword) => keyword.trim() !== '' && bodyLower.includes(keyword.toLowerCase()));
    if (askedForHuman) {
        return { reply: false, reason: 'handoff_keyword' };
    }
    if (input.session !== null) {
        // O teto conta o TOTAL de respostas já dadas nesta sessão
        // (`session.replyCount`, incrementado uma vez por chamada bem-sucedida ao
        // LLM — ver `recordBotReply`), não uma sequência "sem resposta do
        // cliente": uma sessão sobrevive a várias trocas de mensagem enquanto
        // ninguém pede handoff nem estoura um teto, e cada resposta do robô ao
        // longo dela conta para este limite, mesmo que o cliente tenha
        // respondido entre uma e outra.
        if (input.session.replyCount >= input.config.maxRepliesPerSession) {
            return { reply: false, reason: 'max_replies' };
        }
        // O teto de custo por SESSÃO é um limite DURO (ADR-0032) — checado
        // ANTES de gastar mais uma chamada, não depois.
        if (input.session.costMicrosUsd >= input.config.maxCostMicrosUsdPerSession) {
            return { reply: false, reason: 'cost_cap' };
        }
    }
    return { reply: true };
};
/**
 * ============================================================================
 * Follow-up — "comprou ar, lembrar da manutenção em 6 meses" (F053)
 * ============================================================================
 */
/**
 * Soma meses a um instante, em UTC — a mesma ideia de `addMonths` em
 * `packages/finance/src/schedule.ts` (não importada de lá: os dois pacotes
 * são independentes de propósito, e a função é pequena o bastante para não
 * doer duplicar), mas em UTC em vez do fuso local do processo.
 *
 * Isso é o que torna `dueAtFor` insensível a horário de verão: os métodos
 * `getUTC*`/`setUTC*` do `Date` não têm DST — não existe "horário de verão
 * em UTC" — então mutar só os campos de calendário em UTC nunca pisa numa
 * hora que o relógio local pulou ou repetiu. O Brasil não observa DST desde
 * 2019, mas o servidor pode rodar num fuso que observa, e `dueAtFor` não
 * pode depender de onde o worker está implantado.
 *
 * "Todo dia 31" num mês de 30 dias vira o dia 30, não o dia 1 do mês
 * seguinte — o mesmo raciocínio do comentário em `addMonths` de
 * `schedule.ts`.
 */
const addUtcMonths = (from, months) => {
    if (months === 0)
        return new Date(from.getTime());
    const day = from.getUTCDate();
    const target = new Date(from.getTime());
    target.setUTCDate(1);
    target.setUTCMonth(target.getUTCMonth() + months);
    const lastDayOfTargetMonth = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, lastDayOfTargetMonth));
    return target;
};
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Quando uma tarefa de follow-up vence, a partir do instante em que a venda
 * foi concluída — pura, sem `Date.now()` implícito (recebe `saleConcludedAt`
 * como parâmetro, nunca lê o relógio).
 *
 * Meses primeiro (aritmética de calendário, em UTC — ver `addUtcMonths`),
 * depois dias (aritmética de milissegundos, que É correta para "N dias" como
 * uma DURAÇÃO, e não como "o mesmo horário N dias depois" — a diferença só
 * importaria num fuso com DST, e `addUtcMonths` já sidesteps isso operando
 * inteiramente em UTC).
 */
export const dueAtFor = (input) => {
    const withMonths = addUtcMonths(input.saleConcludedAt, input.delay.months);
    return new Date(withMonths.getTime() + input.delay.days * DAY_MS);
};
/**
 * Decide se uma tarefa de follow-up deve ser despachada AGORA — reavaliada a
 * cada disparo do cron, nunca decidida uma vez na materialização (a
 * elegibilidade muda: uma regra pode ser desligada, um modelo pode perder a
 * aprovação, um opt-out pode chegar entre a venda e o vencimento).
 *
 * Cobre, na ordem: a tarefa já não está mais `pending` (defensivo — ver o
 * comentário em `FollowupDeclineReason`); a regra foi desligada; o modelo
 * não está `approved` (fora da janela de 24h, que um follow-up de meses
 * SEMPRE está, só um modelo aprovado serve); o contato não existe (o
 * cliente nunca escreveu por este canal); opt-out ativo.
 */
export const followupEligibility = (input) => {
    if (input.task.status !== 'pending') {
        return { send: false, reason: 'cancelled' };
    }
    if (!input.rule.enabled) {
        return { send: false, reason: 'rule_disabled' };
    }
    if (input.template?.status !== 'approved') {
        return { send: false, reason: 'template_not_approved' };
    }
    if (input.contactId === null) {
        return { send: false, reason: 'skipped_no_contact' };
    }
    if (input.optedOut) {
        return { send: false, reason: 'skipped_optout' };
    }
    return { send: true };
};
/**
 * Se um texto de mensagem recebida pede para sair da lista — a palavra
 * "SAIR" (e variações comuns), sem diferenciar maiúsculas/minúsculas e sem
 * exigir que seja a mensagem inteira ("SAIR", "sair", "quero sair", "pode
 * me tirar da lista, obrigado").
 *
 * Deliberadamente uma lista curta e literal, não uma lista configurável: ao
 * contrário das palavras de handoff do robô (`BotConfig.handoffKeywords`,
 * por tenant), pedir para sair de uma lista de marketing é um direito do
 * titular (LGPD), não uma preferência de atendimento — o mesmo texto tem que
 * funcionar em toda conta, sempre.
 */
/** Palavras isoladas — checadas por TOKEN, não por substring, para que "sair" não bata dentro de outra palavra que o contenha. */
const OPT_OUT_WORDS = new Set(['sair', 'descadastrar', 'descadastre']);
/** Frases — checadas por substring, porque raramente aparecem soltas. */
const OPT_OUT_PHRASES = ['não quero mais receber', 'para de mandar'];
export const isOptOutKeyword = (body) => {
    if (body === null)
        return false;
    const normalized = body.trim().toLowerCase();
    if (normalized === '')
        return false;
    const tokens = normalized.split(/[^\p{L}0-9]+/u).filter(Boolean);
    if (tokens.some((token) => OPT_OUT_WORDS.has(token)))
        return true;
    return OPT_OUT_PHRASES.some((phrase) => normalized.includes(phrase));
};
/**
 * ============================================================================
 * Campanhas e custos — o aviso para muita gente, e a conta no fim do mês (F054)
 * ============================================================================
 */
/**
 * O teto FIXO da coexistência do WhatsApp — 20 mensagens por segundo,
 * compartilhado pela CONTA inteira entre campanha e atendimento ao vivo (o
 * mesmo número que `ChannelAccount.throughputMps` carrega desde a F045).
 * Uma constante aqui, e não só o campo da conta, porque é o valor que
 * `maxCampaignThrottleMps` usa quando nenhum outro teto foi informado.
 */
export const MAX_ACCOUNT_MPS = 20;
/**
 * Quanto uma campanha RESERVA para o atendimento ao vivo, em mensagens por
 * segundo. Uma campanha que usasse o teto inteiro da conta deixaria o
 * inbox — um atendente respondendo um cliente na hora — na fila atrás
 * dela. O número é uma margem deliberada, não calculada: pequeno o
 * bastante para não sufocar uma campanha grande, grande o bastante para o
 * atendimento nunca ficar visivelmente parado atrás de um disparo em massa.
 * Documentado na tela (`/crm-campanhas`), nunca escondido no código —
 * exigência explícita da spec desta feature.
 */
export const CAMPAIGN_RESERVED_MPS = 5;
/**
 * O teto de `throttleMps` que uma campanha pode pedir, dado o teto da
 * CONTA que vai enviá-la. Nunca abaixo de 1 — mesmo uma conta configurada
 * perto do mínimo ainda consegue rodar uma campanha, só que devagar.
 */
export const maxCampaignThrottleMps = (accountThroughputMps) => Math.max(1, accountThroughputMps - CAMPAIGN_RESERVED_MPS);
/**
 * As transições legais de uma campanha, numa tabela — o mesmo desenho de
 * `TICKET_TRANSITIONS`. Ler esta constante é a resposta completa a "o que
 * pode virar o quê".
 *
 * `done`/`cancelled` são terminais: nenhuma campanha sai deles. Pausar
 * (`running → paused`) e retomar (`paused → running`) são o par que faz
 * "pausar no meio" ser uma transição de estado — nunca um `kill` — porque
 * os destinatários `pending` de uma campanha pausada continuam válidos e o
 * despacho, ao retomar, simplesmente volta a reivindicá-los.
 */
export const CAMPAIGN_TRANSITIONS = {
    draft: ['scheduled', 'running', 'cancelled'],
    scheduled: ['running', 'cancelled'],
    running: ['paused', 'done', 'cancelled'],
    paused: ['running', 'cancelled'],
    done: [],
    cancelled: [],
};
const CAMPAIGN_STATUS_LABELS = {
    draft: 'rascunho',
    scheduled: 'agendada',
    running: 'em andamento',
    paused: 'pausada',
    done: 'concluída',
    cancelled: 'cancelada',
};
/**
 * Decide se uma campanha pode ir de `from` para `to` — a única fonte de
 * verdade sobre transições legais, consultada pela rota antes de gravar
 * qualquer coisa. Recusa sempre com `{ code, message }` em português.
 */
export const canTransitionCampaign = (input) => {
    if (input.from === input.to) {
        return {
            allowed: false,
            refusal: {
                code: 'same_status',
                message: `A campanha já está ${CAMPAIGN_STATUS_LABELS[input.from]}.`,
            },
        };
    }
    const allowedTargets = CAMPAIGN_TRANSITIONS[input.from];
    if (!allowedTargets.includes(input.to)) {
        return {
            allowed: false,
            refusal: {
                code: 'transition_not_allowed',
                message: `Uma campanha ${CAMPAIGN_STATUS_LABELS[input.from]} não pode ir direto para ${CAMPAIGN_STATUS_LABELS[input.to]}.`,
            },
        };
    }
    return { allowed: true };
};
/**
 * Decide se ESTE destinatário deve receber a mensagem AGORA — verificado no
 * DESPACHO, por destinatário, nunca na montagem da lista. Entre segmentar e
 * terminar o disparo de uma campanha grande podem passar horas, e um
 * cliente pode escrever "SAIR" nesse intervalo: `optedOut` é lido de novo
 * para cada destinatário no instante exato em que ele seria contatado, não
 * decidido uma vez quando a lista foi montada. `template` é reavaliado pelo
 * mesmo motivo do follow-up (F053): a Meta pode recategorizar ou pausar um
 * modelo entre a criação da campanha e o disparo de fato.
 */
export const campaignRecipientEligibility = (input) => {
    if (input.recipient.status !== 'pending') {
        return { send: false, reason: 'not_pending' };
    }
    if (input.template?.status !== 'approved') {
        return { send: false, reason: 'template_not_approved' };
    }
    if (input.contactId === null) {
        return { send: false, reason: 'no_contact' };
    }
    if (input.optedOut) {
        return { send: false, reason: 'optout' };
    }
    return { send: true };
};
/**
 * A diferença entre o que o Aeris calculou e o que a Meta reportou, em
 * micros de dólar — `null` enquanto a Meta não reportou nada para este
 * período/categoria/tipo (nenhuma integração de reconciliação existe ainda,
 * ver o comentário no topo da migração 0039). Positivo: o Aeris calculou
 * MAIS que a Meta relatou. Negativo: MENOS. Zero: bateram.
 *
 * Uma função pura e deliberadamente simples: a regra inteira desta feature
 * sobre reconciliação é "mostrar, nunca esconder" — não há arredondamento
 * nem tolerância aqui, porque suavizar a diferença é o oposto do que a
 * spec pede.
 */
export const costDivergenceMicrosUsd = (input) => input.metaReportedMicrosUsd === null
    ? null
    : input.aerisMicrosUsd - input.metaReportedMicrosUsd;
//# sourceMappingURL=rules.js.map